import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { fetchMachine, createMachine, updateMachine, clearCurrentMachine } from '../store/slices/machinesSlice';
import { fetchProductsByMachine } from '../store/slices/productsSlice';
import api from '../services/api';

const SHAPE_OPTIONS = [
  { value: 'rectangle', label: 'Rechthoek of vierkant' },
  { value: 'circle', label: 'Cirkel' },
  { value: 'ellipse', label: 'Ellips' },
  { value: 'oval', label: 'Ovaal' },
  { value: 'danish_oval', label: 'Deens ovaal' },
  { value: 'octagon', label: 'Achthoek' },
  { value: 'hexagon', label: 'Zeshoek' },
  { value: 'triangle', label: 'Driehoek' },
  { value: 'organic', label: 'Organisch' },
  { value: 'upload', label: 'Anders' }
];

const MachineForm = ({ onSubmit, loading, initialData, machineId }) => {
  const [formData, setFormData] = useState({
    name: '',
    machineId: '',
    type: 'standard_line',
    location: '',
    supportedShapes: [],
    fixedDimensions: false,
    communication: {
      apiKey: '',
      serverUrl: '',
      csvFolder: ''
    },
    printNode: {
      apiKey: '',
      printerId: ''
    },
    outputMode: {
      csv: true,
      print: false
    },
    status: 'idle'
  });
  const [connectionTest, setConnectionTest] = useState({
    testing: false,
    result: null
  });
  const [printNodeState, setPrintNodeState] = useState({
    printers: [],
    loadingPrinters: false,
    testing: false,
    testResult: null
  });

  // Normalize supportedShapes: convert plain strings to {shape, priority} objects
  const normalizeSupportedShapes = (shapes) => {
    if (!Array.isArray(shapes)) return [];
    return shapes.map(s => typeof s === 'string' ? { shape: s, priority: false } : s);
  };

  // Initialize form with existing data
  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        machineId: initialData.machineId || '',
        type: initialData.type || 'standard_line',
        location: initialData.location || '',
        supportedShapes: normalizeSupportedShapes(initialData.supportedShapes),
        fixedDimensions: initialData.fixedDimensions || false,
        communication: {
          apiKey: initialData.communication?.apiKey || '',
          serverUrl: initialData.communication?.serverUrl || '',
          csvFolder: initialData.communication?.csvFolder || ''
        },
        printNode: {
          apiKey: initialData.printNode?.apiKey || '',
          printerId: initialData.printNode?.printerId || ''
        },
        outputMode: {
          csv: initialData.outputMode?.csv !== false,
          print: initialData.outputMode?.print || false
        },
        status: initialData.status || 'idle'
      });
    }
  }, [initialData]);

  // Test connection handler
  const handleTestConnection = async () => {
    if (!machineId) {
      setConnectionTest({
        testing: false,
        result: { success: false, error: 'Machine moet eerst opgeslagen worden' }
      });
      return;
    }

    setConnectionTest({ testing: true, result: null });

    try {
      const response = await api.post(`/machines/${machineId}/test-connection`);
      setConnectionTest({ testing: false, result: response.data });
    } catch (error) {
      setConnectionTest({
        testing: false,
        result: {
          success: false,
          error: error.response?.data?.error || error.message
        }
      });
    }
  };

  // Fetch PrintNode printers
  const handleFetchPrinters = async () => {
    if (!machineId) return;
    setPrintNodeState(prev => ({ ...prev, loadingPrinters: true }));
    try {
      const response = await api.get(`/machines/${machineId}/printnode/printers`);
      setPrintNodeState(prev => ({ ...prev, loadingPrinters: false, printers: response.data || [] }));
    } catch (error) {
      setPrintNodeState(prev => ({
        ...prev,
        loadingPrinters: false,
        printers: [],
        testResult: { success: false, error: error.response?.data?.error || error.message }
      }));
    }
  };

  // Test PrintNode print
  const handleTestPrint = async () => {
    if (!machineId) return;
    setPrintNodeState(prev => ({ ...prev, testing: true, testResult: null }));
    try {
      const response = await api.post(`/machines/${machineId}/printnode/test`);
      setPrintNodeState(prev => ({ ...prev, testing: false, testResult: response.data }));
    } catch (error) {
      setPrintNodeState(prev => ({
        ...prev,
        testing: false,
        testResult: { success: false, error: error.response?.data?.error || error.message }
      }));
    }
  };

  const types = [
    { value: 'zund', label: 'Zünd snijmachine' },
    { value: 'standard_line', label: 'Standaard lijn' },
    { value: 'manual', label: 'Handmatig' },
    { value: 'printer', label: 'Printer' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleShapeToggle = (shapeValue) => {
    setFormData(prev => {
      const shapes = prev.supportedShapes || [];
      const existing = shapes.find(s => s.shape === shapeValue);
      if (existing) {
        return { ...prev, supportedShapes: shapes.filter(s => s.shape !== shapeValue) };
      } else {
        return { ...prev, supportedShapes: [...shapes, { shape: shapeValue, priority: false }] };
      }
    });
  };

  const handleShapePriorityToggle = (shapeValue) => {
    setFormData(prev => {
      const shapes = (prev.supportedShapes || []).map(s =>
        s.shape === shapeValue ? { ...s, priority: !s.priority } : s
      );
      return { ...prev, supportedShapes: shapes };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Clean up the data
    const submitData = {
      ...formData,
      supportedShapes: formData.supportedShapes || [],
      fixedDimensions: formData.fixedDimensions || false,
      printNode: {
        apiKey: formData.printNode.apiKey || '',
        printerId: formData.printNode.printerId ? parseInt(formData.printNode.printerId, 10) : null
      }
    };

    onSubmit(submitData);
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="row">
        <div className="col-lg-8">
          {/* Basic Info */}
          <div className="card mb-4">
            <div className="card-header">
              <h5 className="mb-0">Basis Informatie</h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">Machine Naam *</label>
                  <input
                    type="text"
                    className="form-control"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="bijv. Zünd G3 L-2500"
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Machine ID *</label>
                  <input
                    type="text"
                    className="form-control"
                    name="machineId"
                    value={formData.machineId}
                    onChange={handleChange}
                    required
                    placeholder="bijv. ZUND-001"
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Type *</label>
                  <select
                    className="form-select"
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    required
                  >
                    {types.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Locatie</label>
                  <input
                    type="text"
                    className="form-control"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    placeholder="bijv. Hal A, Productievloer"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Supported Shapes */}
          <div className="card mb-4">
            <div className="card-header">
              <h5 className="mb-0">Ondersteunde Vormen</h5>
            </div>
            <div className="card-body">
              <p className="text-muted small mb-3">
                Selecteer welke vormen deze machine kan snijden
              </p>
              <div className="row">
                {SHAPE_OPTIONS.map((shape) => {
                  const shapeEntry = (formData.supportedShapes || []).find(s => s.shape === shape.value);
                  const isEnabled = !!shapeEntry;
                  return (
                    <div key={shape.value} className="col-md-6 mb-2">
                      <div
                        className={`card ${isEnabled ? 'border-primary bg-primary bg-opacity-10' : ''}`}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="card-body py-2">
                          <div className="d-flex align-items-center justify-content-between">
                            <div className="d-flex align-items-center" onClick={() => handleShapeToggle(shape.value)}>
                              <input
                                type="checkbox"
                                className="form-check-input me-3"
                                checked={isEnabled}
                                onChange={() => {}}
                              />
                              <div>
                                <div className="fw-medium">{shape.label}</div>
                                <small className="text-muted">{shape.value}</small>
                              </div>
                            </div>
                            {isEnabled && (
                              <div className="form-check form-switch mb-0" onClick={(e) => e.stopPropagation()}>
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  id={`priority-${shape.value}`}
                                  checked={shapeEntry.priority || false}
                                  onChange={() => handleShapePriorityToggle(shape.value)}
                                />
                                <label className="form-check-label small" htmlFor={`priority-${shape.value}`}>
                                  Prioriteit
                                </label>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Fixed Dimensions Option */}
          <div className="card mb-4">
            <div className="card-header">
              <h5 className="mb-0">Afmetingen</h5>
            </div>
            <div className="card-body">
              <div className="form-check form-switch">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="fixedDimensions"
                  name="fixedDimensions"
                  checked={formData.fixedDimensions}
                  onChange={(e) => setFormData(prev => ({ ...prev, fixedDimensions: e.target.checked }))}
                />
                <label className="form-check-label" htmlFor="fixedDimensions">
                  Vaste afmeting
                </label>
              </div>
              <small className="text-muted d-block mt-2">
                Schakel in als deze machine alleen producten met vaste afmetingen kan verwerken
              </small>
            </div>
          </div>

          {/* Output Mode */}
          <div className="card mb-4">
            <div className="card-header">
              <h5 className="mb-0">Output bij Productie</h5>
            </div>
            <div className="card-body">
              <p className="text-muted small mb-3">
                Selecteer welke acties uitgevoerd worden wanneer een snijlijst in productie gaat
              </p>
              <div className="form-check form-switch mb-2">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="outputCsv"
                  checked={formData.outputMode.csv}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    outputMode: { ...prev.outputMode, csv: e.target.checked }
                  }))}
                />
                <label className="form-check-label" htmlFor="outputCsv">
                  <i className="bi bi-file-earmark-text me-2"></i>
                  CSV naar Desktop App
                </label>
                <div><small className="text-muted">Stuurt een CSV-bestand via de Desktop App koppeling</small></div>
              </div>
              <div className="form-check form-switch">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="outputPrint"
                  checked={formData.outputMode.print}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    outputMode: { ...prev.outputMode, print: e.target.checked }
                  }))}
                />
                <label className="form-check-label" htmlFor="outputPrint">
                  <i className="bi bi-printer me-2"></i>
                  Printen via PrintNode
                </label>
                <div><small className="text-muted">Stuurt een printjob naar de geconfigureerde printer</small></div>
              </div>
              {!formData.outputMode.csv && !formData.outputMode.print && (
                <div className="alert alert-warning mt-3 mb-0 py-2 small">
                  <i className="bi bi-exclamation-triangle me-1"></i>
                  Geen output geselecteerd. Er wordt niets verstuurd bij productie.
                </div>
              )}
            </div>
          </div>

          {/* Communication */}
          <div className="card mb-4">
            <div className="card-header">
              <h5 className="mb-0">Desktop App Koppeling</h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-12 mb-3">
                  <label className="form-label">Server URL *</label>
                  <input
                    type="url"
                    className="form-control"
                    name="communication.serverUrl"
                    value={formData.communication.serverUrl}
                    onChange={handleChange}
                    required
                    placeholder="bijv. https://csv-tunnel.example.com"
                  />
                  <small className="text-muted">
                    URL van de CSV Tunnel Server waarmee de desktop app verbonden is
                  </small>
                </div>
                <div className="col-12 mb-3">
                  <label className="form-label">API Key / Desktop Token *</label>
                  <input
                    type="text"
                    className="form-control font-monospace"
                    name="communication.apiKey"
                    value={formData.communication.apiKey}
                    onChange={handleChange}
                    placeholder="Desktop token van de CSV Tunnel Server"
                  />
                  <small className="text-muted">
                    Token gegenereerd door de CSV Tunnel Server voor authenticatie
                  </small>
                </div>
                <div className="col-12 mb-3">
                  <label className="form-label">CSV Map</label>
                  <input
                    type="text"
                    className="form-control"
                    name="communication.csvFolder"
                    value={formData.communication.csvFolder}
                    onChange={handleChange}
                    placeholder="bijv. C:\Snijlijsten of /home/user/csv"
                  />
                  <small className="text-muted">
                    Map op de desktop waar CSV bestanden naartoe gestuurd worden
                  </small>
                </div>
                <div className="col-12">
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={handleTestConnection}
                    disabled={connectionTest.testing || !formData.communication.serverUrl || !formData.communication.apiKey}
                  >
                    {connectionTest.testing ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Verbinding testen...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-plug me-2"></i>
                        Test Connectie
                      </>
                    )}
                  </button>

                  {connectionTest.result && (
                    <div className={`alert mt-3 mb-0 ${connectionTest.result.desktopOnline ? 'alert-success' : connectionTest.result.serverOnline ? 'alert-warning' : 'alert-danger'}`}>
                      {connectionTest.result.desktopOnline ? (
                        <>
                          <i className="bi bi-check-circle-fill me-2"></i>
                          <strong>Verbinding OK!</strong>
                          <ul className="mb-0 mt-2">
                            <li>Server: Online</li>
                            <li>Desktop App: Online ({connectionTest.result.desktopInfo?.desktopName})</li>
                            {connectionTest.result.desktopInfo?.lastSeen && (
                              <li>Laatst gezien: {new Date(connectionTest.result.desktopInfo.lastSeen).toLocaleString('nl-NL')}</li>
                            )}
                            {connectionTest.result.desktopInfo?.requestsProcessed > 0 && (
                              <li>Verwerkte requests: {connectionTest.result.desktopInfo.requestsProcessed}</li>
                            )}
                          </ul>
                        </>
                      ) : connectionTest.result.serverOnline ? (
                        <>
                          <i className="bi bi-exclamation-triangle-fill me-2"></i>
                          <strong>Desktop App Offline</strong>
                          <p className="mb-0 mt-2">
                            De CSV Tunnel Server is bereikbaar, maar de desktop app is niet verbonden.
                            Start de desktop app en controleer of deze correct geconfigureerd is.
                          </p>
                        </>
                      ) : (
                        <>
                          <i className="bi bi-x-circle-fill me-2"></i>
                          <strong>Verbinding mislukt</strong>
                          <p className="mb-0 mt-2">{connectionTest.result.error}</p>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* PrintNode */}
          <div className="card mb-4">
            <div className="card-header">
              <h5 className="mb-0">
                <i className="bi bi-printer me-2"></i>
                PrintNode Koppeling
              </h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-12 mb-3">
                  <label className="form-label">PrintNode API Key</label>
                  <input
                    type="text"
                    className="form-control font-monospace"
                    name="printNode.apiKey"
                    value={formData.printNode.apiKey}
                    onChange={handleChange}
                    placeholder="PrintNode API key"
                  />
                  <small className="text-muted">
                    Te vinden op <a href="https://app.printnode.com/app/apikeys" target="_blank" rel="noreferrer">app.printnode.com/app/apikeys</a>
                  </small>
                </div>
                <div className="col-12 mb-3">
                  <label className="form-label">Printer</label>
                  <div className="input-group">
                    <select
                      className="form-select"
                      name="printNode.printerId"
                      value={formData.printNode.printerId}
                      onChange={handleChange}
                    >
                      <option value="">Selecteer printer...</option>
                      {printNodeState.printers.map((printer) => (
                        <option key={printer.id} value={printer.id}>
                          {printer.name} ({printer.computer?.name || 'Onbekend'})
                          {printer.state === 'online' ? '' : ' - offline'}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={handleFetchPrinters}
                      disabled={!formData.printNode.apiKey || printNodeState.loadingPrinters || !machineId}
                      title="Printers ophalen"
                    >
                      {printNodeState.loadingPrinters ? (
                        <span className="spinner-border spinner-border-sm"></span>
                      ) : (
                        <i className="bi bi-arrow-clockwise"></i>
                      )}
                    </button>
                  </div>
                  <small className="text-muted">
                    Sla eerst op en klik op de refresh-knop om printers op te halen
                  </small>
                </div>
                <div className="col-12">
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={handleTestPrint}
                    disabled={!formData.printNode.apiKey || !formData.printNode.printerId || printNodeState.testing || !machineId}
                  >
                    {printNodeState.testing ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Testpagina printen...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-printer me-2"></i>
                        Test Print
                      </>
                    )}
                  </button>

                  {printNodeState.testResult && (
                    <div className={`alert mt-3 mb-0 ${printNodeState.testResult.success ? 'alert-success' : 'alert-danger'}`}>
                      {printNodeState.testResult.success ? (
                        <>
                          <i className="bi bi-check-circle-fill me-2"></i>
                          <strong>Testpagina verstuurd!</strong>
                          <ul className="mb-0 mt-2">
                            <li>Printer: {printNodeState.testResult.printer?.name}</li>
                            <li>Computer: {printNodeState.testResult.printer?.computer}</li>
                            <li>Print Job ID: {printNodeState.testResult.printJobId}</li>
                          </ul>
                        </>
                      ) : (
                        <>
                          <i className="bi bi-x-circle-fill me-2"></i>
                          <strong>Print mislukt</strong>
                          <p className="mb-0 mt-2">{printNodeState.testResult.error}</p>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-lg-4">
          {/* Submit */}
          <div className="card">
            <div className="card-body d-grid gap-2">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Opslaan...
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-lg me-2"></i>
                    Machine Opslaan
                  </>
                )}
              </button>
              <Link to="/machines" className="btn btn-outline-secondary">
                Annuleren
              </Link>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};

const MachineDetail = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { currentMachine: machine, loading, error } = useSelector((state) => state.machines);
  const { products, pagination, loading: productsLoading } = useSelector((state) => state.products);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [productPage, setProductPage] = useState(1);
  const [productSearch, setProductSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  // Product linking state
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [availableLoading, setAvailableLoading] = useState(false);
  const [availableSearch, setAvailableSearch] = useState('');
  const [availablePagination, setAvailablePagination] = useState({ page: 1, total: 0, totalPages: 0 });
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [linkAsPrimary, setLinkAsPrimary] = useState(false);

  // Product action modal & multi-select state
  const [actionProduct, setActionProduct] = useState(null);
  const [checkedProductIds, setCheckedProductIds] = useState([]);

  const isNew = id === 'new';

  useEffect(() => {
    if (isNew) {
      dispatch(clearCurrentMachine());
    } else {
      dispatch(fetchMachine(id));
    }
  }, [dispatch, id, isNew]);

  // Fetch products when page or search changes
  useEffect(() => {
    if (!isNew && id) {
      dispatch(fetchProductsByMachine({ machineId: id, page: productPage, search: productSearch }));
    }
  }, [dispatch, id, isNew, productPage, productSearch]);

  const handleSearch = (e) => {
    e.preventDefault();
    setProductSearch(searchInput);
    setProductPage(1);
  };

  const clearSearch = () => {
    setSearchInput('');
    setProductSearch('');
    setProductPage(1);
  };

  const handleCreateMachine = async (data) => {
    setSubmitLoading(true);
    try {
      const result = await dispatch(createMachine(data)).unwrap();
      navigate(`/machines/${result._id}`);
    } catch (err) {
      console.error('Failed to create machine:', err);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleUpdateMachine = async (data) => {
    setSubmitLoading(true);
    try {
      await dispatch(updateMachine({ id, data })).unwrap();
      setIsEditing(false);
    } catch (err) {
      console.error('Failed to update machine:', err);
    } finally {
      setSubmitLoading(false);
    }
  };

  // Fetch available products for linking
  const fetchAvailableProducts = async (search = '', page = 1) => {
    setAvailableLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (search) params.append('search', search);

      const response = await api.get(`/machines/${id}/products/available?${params}`);
      setAvailableProducts(response.data.products || []);
      setAvailablePagination(response.data.pagination || { page: 1, total: 0, totalPages: 0 });
    } catch (err) {
      console.error('Failed to fetch available products:', err);
    } finally {
      setAvailableLoading(false);
    }
  };

  // Open link modal
  const openLinkModal = () => {
    setShowLinkModal(true);
    setSelectedProducts([]);
    setAvailableSearch('');
    setLinkAsPrimary(false);
    fetchAvailableProducts();
  };

  // Handle product selection toggle
  const toggleProductSelection = (productId) => {
    setSelectedProducts(prev =>
      prev.includes(productId)
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  // Select all products on current page
  const selectAllProductsOnPage = () => {
    const allIds = availableProducts.map(p => p._id);
    setSelectedProducts(prev => {
      const newSelection = [...prev];
      allIds.forEach(id => {
        if (!newSelection.includes(id)) {
          newSelection.push(id);
        }
      });
      return newSelection;
    });
  };

  // Select ALL available products (fetch all IDs from server)
  const selectAllProducts = async () => {
    try {
      setAvailableLoading(true);
      // Fetch all available product IDs (not linked to this machine)
      const response = await api.get(`/machines/${id}/products/available`, {
        params: { limit: 10000, search: availableSearch }
      });
      const allIds = response.data.products.map(p => p._id);
      setSelectedProducts(allIds);
    } catch (error) {
      console.error('Error fetching all products:', error);
    } finally {
      setAvailableLoading(false);
    }
  };

  // Deselect all products
  const deselectAllProducts = () => {
    setSelectedProducts([]);
  };

  // Link selected products
  const handleLinkProducts = async () => {
    if (selectedProducts.length === 0) return;

    setSubmitLoading(true);
    try {
      await api.post(`/machines/${id}/products`, {
        productIds: selectedProducts,
        asPrimary: linkAsPrimary
      });
      setShowLinkModal(false);
      // Refresh products list
      dispatch(fetchProductsByMachine({ machineId: id, page: 1, search: '' }));
      setProductPage(1);
      setProductSearch('');
      setSearchInput('');
    } catch (err) {
      console.error('Failed to link products:', err);
    } finally {
      setSubmitLoading(false);
    }
  };

  // Unlink a product
  const handleUnlinkProduct = async (productId) => {
    if (!window.confirm('Weet je zeker dat je dit product wilt ontkoppelen van deze machine?')) {
      return;
    }

    try {
      await api.delete(`/machines/${id}/products`, {
        data: { productIds: [productId] }
      });
      dispatch(fetchProductsByMachine({ machineId: id, page: productPage, search: productSearch }));
    } catch (err) {
      console.error('Failed to unlink product:', err);
    }
  };

  const handleSetPrimary = async (productIds) => {
    const ids = Array.isArray(productIds) ? productIds : [productIds];
    try {
      await Promise.all(ids.map(pid => api.put(`/machines/${id}/products/${pid}/primary`)));
      dispatch(fetchProductsByMachine({ machineId: id, page: productPage, search: productSearch }));
      setActionProduct(null);
      setCheckedProductIds([]);
    } catch (err) {
      console.error('Failed to set primary:', err);
    }
  };

  const handleRemovePrimary = async (productIds) => {
    const ids = Array.isArray(productIds) ? productIds : [productIds];
    try {
      await Promise.all(ids.map(pid => api.delete(`/machines/${id}/products/${pid}/primary`)));
      dispatch(fetchProductsByMachine({ machineId: id, page: productPage, search: productSearch }));
      setActionProduct(null);
      setCheckedProductIds([]);
    } catch (err) {
      console.error('Failed to remove primary:', err);
    }
  };

  const handleBulkUnlink = async (productIds) => {
    const ids = Array.isArray(productIds) ? productIds : [productIds];
    if (!window.confirm(`Weet je zeker dat je ${ids.length} product(en) wilt ontkoppelen?`)) return;
    try {
      await api.delete(`/machines/${id}/products`, { data: { productIds: ids } });
      dispatch(fetchProductsByMachine({ machineId: id, page: productPage, search: productSearch }));
      setCheckedProductIds([]);
    } catch (err) {
      console.error('Failed to unlink products:', err);
    }
  };

  const toggleCheckedProduct = (productId) => {
    setCheckedProductIds(prev =>
      prev.includes(productId) ? prev.filter(id => id !== productId) : [...prev, productId]
    );
  };

  const toggleAllChecked = () => {
    if (!products) return;
    const linkableIds = products
      .filter(p => {
        const isPrimary = p.primaryMachine?._id?.toString() === machine?._id?.toString() ||
                          p.primaryMachine?.toString() === machine?._id?.toString();
        const isCompatible = p.compatibleMachines?.some(m =>
          (m._id?.toString() || m.toString()) === machine?._id?.toString()
        );
        return isPrimary || isCompatible;
      })
      .map(p => p._id);
    if (checkedProductIds.length === linkableIds.length) {
      setCheckedProductIds([]);
    } else {
      setCheckedProductIds(linkableIds);
    }
  };

  // Show form for new machine
  if (isNew) {
    return (
      <div className="machine-detail">
        <div className="page-header">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item"><Link to="/machines">Machines</Link></li>
              <li className="breadcrumb-item active">Nieuwe machine</li>
            </ol>
          </nav>
          <h1>Nieuwe Machine Toevoegen</h1>
        </div>

        {error && (
          <div className="alert alert-danger">{error}</div>
        )}

        <MachineForm onSubmit={handleCreateMachine} loading={submitLoading} />
      </div>
    );
  }

  // Show edit form
  if (isEditing && machine) {
    return (
      <div className="machine-detail">
        <div className="page-header">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item"><Link to="/machines">Machines</Link></li>
              <li className="breadcrumb-item"><Link to={`/machines/${id}`} onClick={(e) => { e.preventDefault(); setIsEditing(false); }}>{machine.name}</Link></li>
              <li className="breadcrumb-item active">Bewerken</li>
            </ol>
          </nav>
          <h1>Machine Bewerken</h1>
        </div>

        {error && (
          <div className="alert alert-danger">{error}</div>
        )}

        <MachineForm onSubmit={handleUpdateMachine} loading={submitLoading} initialData={machine} machineId={id} />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Laden...</span>
        </div>
      </div>
    );
  }

  if (!machine) {
    return (
      <div className="alert alert-warning">
        Machine niet gevonden
        <Link to="/machines" className="btn btn-link">Terug naar machines</Link>
      </div>
    );
  }

  return (
    <div className="machine-detail">
      <div className="page-header">
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><Link to="/machines">Machines</Link></li>
            <li className="breadcrumb-item active">{machine.name}</li>
          </ol>
        </nav>
        <div className="d-flex align-items-center gap-2">
          <h1>{machine.name}</h1>
          {machine.isDefault && (
            <span className="badge bg-primary fs-6">Standaard Machine</span>
          )}
        </div>
      </div>

      <div className="row">
        <div className="col-lg-8">
          {/* Machine Info */}
          <div className="card mb-4">
            <div className="card-header">
              <h5 className="mb-0">Machine Informatie</h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="text-muted">Type</label>
                  <p className="mb-0 fw-medium">{machine.type || '-'}</p>
                </div>
                <div className="col-md-6 mb-3">
                  <label className="text-muted">Locatie</label>
                  <p className="mb-0 fw-medium">{machine.location || '-'}</p>
                </div>
                <div className="col-md-6 mb-3">
                  <label className="text-muted">Vaste afmeting</label>
                  <p className="mb-0 fw-medium">
                    {machine.fixedDimensions ? (
                      <span className="badge bg-success">Ja</span>
                    ) : (
                      <span className="badge bg-secondary">Nee</span>
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Supported Shapes */}
          <div className="card mb-4">
            <div className="card-header">
              <h5 className="mb-0">Ondersteunde Vormen</h5>
            </div>
            <div className="card-body">
              {machine.supportedShapes && machine.supportedShapes.length > 0 ? (
                <div className="d-flex flex-wrap gap-2">
                  {machine.supportedShapes.map((entry) => {
                    const shapeValue = typeof entry === 'string' ? entry : entry.shape;
                    const hasPriority = typeof entry === 'object' && entry.priority;
                    const shapeInfo = SHAPE_OPTIONS.find(s => s.value === shapeValue);
                    return (
                      <span key={shapeValue} className={`badge fs-6 ${hasPriority ? 'bg-warning text-dark' : 'bg-primary'}`}>
                        {hasPriority && <i className="bi bi-star-fill me-1"></i>}
                        {shapeInfo?.label || shapeValue}
                      </span>
                    );
                  })}
                </div>
              ) : (
                <p className="text-muted mb-0">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  Geen vormen geconfigureerd. Klik op "Machine bewerken" om vormen toe te voegen.
                </p>
              )}
            </div>
          </div>

          {/* Compatible Products */}
          <div className="card mb-4">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                Gekoppelde Producten
                {pagination?.total > 0 && (
                  <span className="badge bg-secondary ms-2">{pagination.total}</span>
                )}
              </h5>
              <div className="d-flex gap-2">
                <button
                  className="btn btn-sm btn-primary"
                  onClick={openLinkModal}
                >
                  <i className="bi bi-plus-lg me-1"></i>
                  Producten koppelen
                </button>
                <Link to="/products" className="btn btn-sm btn-outline-secondary">
                  Alle producten
                </Link>
              </div>
            </div>
            {machine.isDefault && (
              <div className="card-body border-bottom bg-light py-2">
                <small className="text-muted">
                  <i className="bi bi-info-circle me-1"></i>
                  Dit is de standaard machine. Alle producten zonder specifieke machine-toewijzing worden hier getoond.
                </small>
              </div>
            )}
            {/* Search */}
            <div className="card-body border-bottom">
              <form onSubmit={handleSearch} className="d-flex gap-2">
                <div className="input-group">
                  <span className="input-group-text">
                    <i className="bi bi-search"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Zoek op naam of code..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />
                </div>
                <button type="submit" className="btn btn-primary">Zoeken</button>
                {productSearch && (
                  <button type="button" className="btn btn-outline-secondary" onClick={clearSearch}>
                    <i className="bi bi-x-lg"></i>
                  </button>
                )}
              </form>
            </div>
            <div className="card-body p-0">
              {products?.length > 0 ? (
                <div className="table-responsive">
                  {checkedProductIds.length > 0 && (
                    <div className="d-flex align-items-center gap-2 p-2 bg-light border-bottom">
                      <span className="text-muted me-2">
                        <strong>{checkedProductIds.length}</strong> geselecteerd
                      </span>
                      <button
                        className="btn btn-sm btn-outline-warning"
                        onClick={() => handleSetPrimary(checkedProductIds)}
                      >
                        <i className="bi bi-star-fill me-1"></i>
                        Primair maken
                      </button>
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => handleRemovePrimary(checkedProductIds)}
                      >
                        <i className="bi bi-star me-1"></i>
                        Primair verwijderen
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleBulkUnlink(checkedProductIds)}
                      >
                        <i className="bi bi-x-lg me-1"></i>
                        Ontkoppelen
                      </button>
                      <button
                        className="btn btn-sm btn-link text-muted ms-auto"
                        onClick={() => setCheckedProductIds([])}
                      >
                        Deselecteer
                      </button>
                    </div>
                  )}
                  <table className="table table-hover mb-0">
                    <thead>
                      <tr>
                        <th style={{ width: '40px' }}>
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={checkedProductIds.length > 0 && checkedProductIds.length === products.filter(p => {
                              const ip = p.primaryMachine?._id?.toString() === machine._id?.toString() ||
                                         p.primaryMachine?.toString() === machine._id?.toString();
                              const ic = p.compatibleMachines?.some(m =>
                                (m._id?.toString() || m.toString()) === machine._id?.toString()
                              );
                              return ip || ic;
                            }).length}
                            onChange={toggleAllChecked}
                          />
                        </th>
                        <th>Product</th>
                        <th>Code</th>
                        <th>Materiaal</th>
                        <th>Toewijzing</th>
                        <th style={{ width: '50px' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {products.map((product) => {
                        const isPrimary = product.primaryMachine?._id?.toString() === machine._id?.toString() ||
                                         product.primaryMachine?.toString() === machine._id?.toString();
                        const isCompatible = product.compatibleMachines?.some(m =>
                          (m._id?.toString() || m.toString()) === machine._id?.toString()
                        );
                        const isUnassigned = !product.primaryMachine;
                        const isLinked = isPrimary || isCompatible;

                        return (
                          <tr key={product._id} className={checkedProductIds.includes(product._id) ? 'table-active' : ''}>
                            <td>
                              {isLinked && (
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  checked={checkedProductIds.includes(product._id)}
                                  onChange={() => toggleCheckedProduct(product._id)}
                                />
                              )}
                            </td>
                            <td>
                              <Link to={`/products/${product._id}`}>
                                {product.name}
                              </Link>
                            </td>
                            <td><code>{product.productCode}</code></td>
                            <td>
                              <span className="badge bg-light text-dark">
                                {product.materialType || '-'}
                              </span>
                            </td>
                            <td>
                              {isPrimary ? (
                                <span className="badge bg-success">
                                  <i className="bi bi-star-fill me-1"></i>
                                  Primair
                                </span>
                              ) : isCompatible ? (
                                <span className="badge bg-info">
                                  <i className="bi bi-check me-1"></i>
                                  Compatibel
                                </span>
                              ) : isUnassigned && machine.isDefault ? (
                                <span className="badge bg-secondary">
                                  <i className="bi bi-dash me-1"></i>
                                  Standaard
                                </span>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>
                              {isLinked && (
                                <button
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={() => setActionProduct({ ...product, isPrimary, isCompatible })}
                                  title="Acties"
                                >
                                  <i className="bi bi-three-dots-vertical"></i>
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-4 text-muted">
                  {productsLoading ? (
                    <div className="spinner-border spinner-border-sm" role="status">
                      <span className="visually-hidden">Laden...</span>
                    </div>
                  ) : productSearch ? (
                    `Geen producten gevonden voor "${productSearch}"`
                  ) : (
                    'Geen producten gekoppeld aan deze machine'
                  )}
                </div>
              )}
            </div>
            {/* Pagination */}
            {pagination?.totalPages > 1 && (
              <div className="card-footer">
                <div className="d-flex justify-content-between align-items-center">
                  <small className="text-muted">
                    Pagina {pagination.page} van {pagination.totalPages} ({pagination.total} producten)
                  </small>
                  <nav>
                    <ul className="pagination pagination-sm mb-0">
                      <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => setProductPage(1)}
                          disabled={pagination.page === 1}
                        >
                          <i className="bi bi-chevron-double-left"></i>
                        </button>
                      </li>
                      <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => setProductPage(p => p - 1)}
                          disabled={pagination.page === 1}
                        >
                          <i className="bi bi-chevron-left"></i>
                        </button>
                      </li>
                      <li className="page-item disabled">
                        <span className="page-link">{pagination.page}</span>
                      </li>
                      <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => setProductPage(p => p + 1)}
                          disabled={pagination.page === pagination.totalPages}
                        >
                          <i className="bi bi-chevron-right"></i>
                        </button>
                      </li>
                      <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => setProductPage(pagination.totalPages)}
                          disabled={pagination.page === pagination.totalPages}
                        >
                          <i className="bi bi-chevron-double-right"></i>
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>
            )}
          </div>

          {/* Performance */}
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">Performance</h5>
            </div>
            <div className="card-body">
              <div className="row text-center">
                <div className="col-md-4 mb-3">
                  <h3 className="text-primary">{machine.performance?.totalJobs || 0}</h3>
                  <p className="text-muted mb-0">Jobs voltooid</p>
                </div>
                <div className="col-md-4 mb-3">
                  <h3 className="text-success">
                    {machine.performance?.totalJobs
                      ? Math.round((machine.performance.successfulJobs / machine.performance.totalJobs) * 100)
                      : 0}%
                  </h3>
                  <p className="text-muted mb-0">Succesrate</p>
                </div>
                <div className="col-md-4 mb-3">
                  <h3 className="text-warning">{machine.performance?.averageProcessingTime || 0}m</h3>
                  <p className="text-muted mb-0">Gem. verwerkingstijd</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-lg-4">
          {/* Status Card */}
          <div className="card mb-4">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <span className="text-muted">Status</span>
                <span className={`badge bg-${
                  machine.status === 'active' ? 'success' :
                  machine.status === 'maintenance' ? 'warning' :
                  machine.status === 'error' ? 'danger' : 'secondary'
                }`}>
                  {machine.status === 'active' ? 'Actief' :
                   machine.status === 'maintenance' ? 'Onderhoud' :
                   machine.status === 'error' ? 'Storing' :
                   machine.status === 'idle' ? 'Inactief' : machine.status}
                </span>
              </div>
            </div>
          </div>

          {/* Current Job & Queue */}
          <div className="card mb-4">
            <div className="card-header">
              <h6 className="mb-0">
                <i className="bi bi-list-task me-2"></i>
                Jobs in Productie
              </h6>
            </div>
            <div className="card-body p-0">
              {machine.currentJob ? (
                <div className="list-group list-group-flush">
                  {/* Current Job */}
                  <div className="list-group-item bg-success bg-opacity-10">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <span className="badge bg-success me-2">Actief</span>
                        <Link to={`/cutlists/${machine.currentJob._id}`} className="fw-medium">
                          {machine.currentJob.name || machine.currentJob.cutListId}
                        </Link>
                      </div>
                      <span className="badge bg-secondary">
                        {machine.currentJob.items?.length || 0} items
                      </span>
                    </div>
                  </div>

                  {/* Queue */}
                  {machine.queue && machine.queue.length > 0 && (
                    <>
                      {machine.queue.map((job, index) => (
                        <div key={job._id} className="list-group-item">
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <span className="badge bg-warning text-dark me-2">#{index + 1}</span>
                              <Link to={`/cutlists/${job._id}`}>
                                {job.name || job.cutListId}
                              </Link>
                            </div>
                            <span className="badge bg-secondary">
                              {job.items?.length || 0} items
                            </span>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              ) : (
                <div className="text-center py-4 text-muted">
                  <i className="bi bi-inbox fs-4 d-block mb-2"></i>
                  Geen actieve jobs
                </div>
              )}
            </div>
            {(machine.currentJob || (machine.queue && machine.queue.length > 0)) && (
              <div className="card-footer bg-transparent">
                <small className="text-muted">
                  Totaal: {1 + (machine.queue?.length || 0)} snijlijst(en) in productie
                </small>
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="card">
            <div className="card-header">
              <h6 className="mb-0">Acties</h6>
            </div>
            <div className="card-body d-grid gap-2">
              <Link
                to={`/cutlists?machine=${machine._id}`}
                className="btn btn-outline-primary"
              >
                <i className="bi bi-scissors me-2"></i>
                Snijlijsten bekijken
              </Link>
              <button
                className="btn btn-outline-secondary"
                onClick={() => setIsEditing(true)}
              >
                <i className="bi bi-pencil me-2"></i>
                Machine bewerken
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Product Link Modal */}
      {/* Product action modal */}
      {actionProduct && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.3)' }} onClick={() => setActionProduct(null)}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '400px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-content shadow-lg border-0" style={{ borderRadius: '12px', overflow: 'hidden' }}>
              <div className="modal-body p-4">
                <div className="d-flex align-items-start mb-3">
                  <div className="flex-grow-1">
                    <h6 className="mb-1">{actionProduct.name}</h6>
                    <small className="text-muted">
                      <code>{actionProduct.productCode}</code>
                      {actionProduct.materialType && <span className="ms-2">{actionProduct.materialType}</span>}
                    </small>
                  </div>
                  <button type="button" className="btn-close" onClick={() => setActionProduct(null)}></button>
                </div>

                <div className="mb-2">
                  <small className="text-uppercase text-muted fw-semibold" style={{ fontSize: '0.7rem', letterSpacing: '0.05em' }}>
                    Huidige status
                  </small>
                  <div className="mt-1">
                    {actionProduct.isPrimary ? (
                      <span className="badge bg-success"><i className="bi bi-star-fill me-1"></i>Primair</span>
                    ) : (
                      <span className="badge bg-info"><i className="bi bi-check me-1"></i>Compatibel</span>
                    )}
                  </div>
                </div>

                <hr className="my-3" />

                <div className="d-grid gap-2">
                  {!actionProduct.isPrimary && (
                    <button
                      className="btn btn-outline-warning d-flex align-items-center"
                      onClick={() => handleSetPrimary(actionProduct._id)}
                    >
                      <i className="bi bi-star-fill me-2"></i>
                      <span>Instellen als primair</span>
                    </button>
                  )}
                  {actionProduct.isPrimary && (
                    <button
                      className="btn btn-outline-secondary d-flex align-items-center"
                      onClick={() => handleRemovePrimary(actionProduct._id)}
                    >
                      <i className="bi bi-star me-2"></i>
                      <span>Primair verwijderen</span>
                    </button>
                  )}
                  <button
                    className="btn btn-outline-danger d-flex align-items-center"
                    onClick={() => { setActionProduct(null); handleUnlinkProduct(actionProduct._id); }}
                  >
                    <i className="bi bi-x-lg me-2"></i>
                    <span>Ontkoppelen van machine</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showLinkModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Producten koppelen aan {machine.name}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowLinkModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                {/* Search */}
                <div className="mb-3">
                  <div className="input-group">
                    <span className="input-group-text">
                      <i className="bi bi-search"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Zoek op naam of productcode..."
                      value={availableSearch}
                      onChange={(e) => {
                        setAvailableSearch(e.target.value);
                        fetchAvailableProducts(e.target.value, 1);
                      }}
                    />
                  </div>
                </div>

                {/* Options */}
                <div className="mb-3">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="linkAsPrimary"
                      checked={linkAsPrimary}
                      onChange={(e) => setLinkAsPrimary(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="linkAsPrimary">
                      Instellen als primaire machine voor geselecteerde producten
                    </label>
                  </div>
                </div>

                {/* Selection controls */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    {selectedProducts.length > 0 && (
                      <span className="text-primary">
                        <i className="bi bi-check-circle me-1"></i>
                        {selectedProducts.length} product(en) geselecteerd
                      </span>
                    )}
                  </div>
                  <div className="btn-group btn-group-sm">
                    <button
                      className="btn btn-outline-primary"
                      onClick={selectAllProductsOnPage}
                      disabled={availableProducts.length === 0 || availableLoading}
                    >
                      <i className="bi bi-check-all me-1"></i>
                      Selecteer pagina
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={selectAllProducts}
                      disabled={availablePagination.total === 0 || availableLoading}
                    >
                      <i className="bi bi-check2-all me-1"></i>
                      Selecteer alles ({availablePagination.total})
                    </button>
                    {selectedProducts.length > 0 && (
                      <button
                        className="btn btn-outline-secondary"
                        onClick={deselectAllProducts}
                      >
                        <i className="bi bi-x-lg me-1"></i>
                        Deselecteer
                      </button>
                    )}
                  </div>
                </div>

                {/* Product list */}
                {availableLoading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border spinner-border-sm" role="status">
                      <span className="visually-hidden">Laden...</span>
                    </div>
                  </div>
                ) : availableProducts.length === 0 ? (
                  <div className="text-center py-4 text-muted">
                    {availableSearch
                      ? `Geen beschikbare producten gevonden voor "${availableSearch}"`
                      : 'Alle producten zijn al gekoppeld aan deze machine'}
                  </div>
                ) : (
                  <div className="list-group">
                    {availableProducts.map((product) => (
                      <label
                        key={product._id}
                        className={`list-group-item list-group-item-action d-flex align-items-center ${
                          selectedProducts.includes(product._id) ? 'active' : ''
                        }`}
                        style={{ cursor: 'pointer' }}
                      >
                        <input
                          type="checkbox"
                          className="form-check-input me-3"
                          checked={selectedProducts.includes(product._id)}
                          onChange={() => toggleProductSelection(product._id)}
                        />
                        <div className="flex-grow-1">
                          <div className="fw-medium">{product.name}</div>
                          <small className={selectedProducts.includes(product._id) ? 'text-white-50' : 'text-muted'}>
                            {product.productCode}
                            {product.materialType && ` • ${product.materialType}`}
                            {product.parts?.length > 0 && ` • ${product.parts.length} onderdelen`}
                          </small>
                        </div>
                      </label>
                    ))}
                  </div>
                )}

                {/* Pagination */}
                {availablePagination.totalPages > 1 && (
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <small className="text-muted">
                      Pagina {availablePagination.page} van {availablePagination.totalPages}
                    </small>
                    <nav>
                      <ul className="pagination pagination-sm mb-0">
                        <li className={`page-item ${availablePagination.page === 1 ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => fetchAvailableProducts(availableSearch, availablePagination.page - 1)}
                            disabled={availablePagination.page === 1}
                          >
                            <i className="bi bi-chevron-left"></i>
                          </button>
                        </li>
                        <li className={`page-item ${availablePagination.page === availablePagination.totalPages ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => fetchAvailableProducts(availableSearch, availablePagination.page + 1)}
                            disabled={availablePagination.page === availablePagination.totalPages}
                          >
                            <i className="bi bi-chevron-right"></i>
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowLinkModal(false)}
                >
                  Annuleren
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleLinkProducts}
                  disabled={selectedProducts.length === 0 || submitLoading}
                >
                  {submitLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Koppelen...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-link-45deg me-2"></i>
                      {selectedProducts.length} product(en) koppelen
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MachineDetail;
